import {useEffect, useMemo, useRef, useState} from 'react';
import {useVirtualizer} from '@tanstack/react-virtual';
import {Badge, Spinner} from 'react-bootstrap';
import {FaChevronDown, FaChevronRight, FaCommentDots, FaInbox, FaPlus, FaSyncAlt, FaTimes} from 'react-icons/fa';
import {conversationDisplayName, conversationUnread} from '../chatUtils';
import {ConversationIcon} from '../domain/conversationPresentation';

function ConversationButton({conversation, currentUserId, selectedId, onSelect, style}) {
    const unread = conversationUnread(conversation);
    return (
        <button
            type="button"
            style={style}
            className={`chat-conversation-link${conversation.id === selectedId ? ' is-selected' : ''}${unread ? ' has-unread' : ''}`}
            onClick={() => onSelect(conversation)}
        >
            <ConversationIcon kind={conversation.kind} />
            <span>{conversationDisplayName(conversation, currentUserId)}</span>
            {unread ? <Badge bg="light" text="dark" pill>{unread}</Badge> : null}
        </button>
    );
}

function VirtualConversationSection({title, conversations, currentUserId, selectedId, onSelect, onLoadMore, hasMore, loading}) {
    const scrollRef = useRef(null);
    const virtualizer = useVirtualizer({
        count: conversations.length,
        getScrollElement: () => scrollRef.current,
        estimateSize: () => 38,
        overscan: 10,
    });
    return (
        <section className="chat-sidebar-section chat-conversation-results">
            <h2>{title}</h2>
            <div ref={scrollRef} className="chat-conversation-virtual-scroll">
                <div className="chat-conversation-virtual-space" style={{height: `${virtualizer.getTotalSize()}px`}}>
                    {virtualizer.getVirtualItems().map(row => (
                        <ConversationButton
                            key={conversations[row.index].id}
                            conversation={conversations[row.index]}
                            currentUserId={currentUserId}
                            selectedId={selectedId}
                            onSelect={onSelect}
                            style={{position: 'absolute', transform: `translateY(${row.start}px)`}}
                        />
                    ))}
                </div>
            </div>
            {hasMore ? (
                <button type="button" className="chat-load-more" disabled={loading} onClick={onLoadMore}>
                    {loading ? <Spinner size="sm" /> : 'Load more'}
                </button>
            ) : null}
        </section>
    );
}

function mergeResults(current, incoming) {
    const byId = new Map((current || []).map(item => [item.id, item]));
    for (const item of incoming || []) byId.set(item.id, {...byId.get(item.id), ...item});
    return [...byId.values()];
}

export default function ChatSidebar({
    conversations,
    conversationGroups,
    currentUserId,
    selectedId,
    onSelect,
    onCreate,
    onInbox,
    inboxOpen,
    onRefresh,
    onLoadMore,
    hasMore,
    onSearch,
    onLoadGroups,
    refreshing,
    open,
    onClose,
}) {
    const [search, setSearch] = useState('');
    const normalizedSearch = search.trim();
    const [remoteResults, setRemoteResults] = useState(null);
    const [remoteCursor, setRemoteCursor] = useState('');
    const [remoteHasMore, setRemoteHasMore] = useState(false);
    const [remoteLoading, setRemoteLoading] = useState(false);
    const [agentsOpen, setAgentsOpen] = useState(false);
    const [activeAgent, setActiveAgent] = useState(null);
    const requestVersionRef = useRef(0);
    const selectionVersionRef = useRef(0);

    useEffect(() => () => { requestVersionRef.current += 1; }, []);

    const selectConversation = conversation => {
        selectionVersionRef.current += 1;
        onSelect(conversation);
    };

    useEffect(() => {
        if (normalizedSearch.length < 2) return undefined;
        const requestVersion = ++requestVersionRef.current;
        const abortController = new AbortController();
        const timer = window.setTimeout(() => {
            setRemoteLoading(true);
            onSearch({query: normalizedSearch, signal: abortController.signal})
                .then(payload => {
                    if (requestVersion !== requestVersionRef.current) return;
                    setRemoteResults(payload.conversations || []);
                    setRemoteCursor(payload.next_cursor || '');
                    setRemoteHasMore(Boolean(payload.has_more));
                })
                .catch(error => {
                    if (requestVersion === requestVersionRef.current && error?.name !== 'AbortError') {
                        setRemoteResults([]);
                    }
                })
                .finally(() => {
                    if (requestVersion === requestVersionRef.current) setRemoteLoading(false);
                });
        }, 300);
        return () => {
            window.clearTimeout(timer);
            abortController.abort();
            if (requestVersion === requestVersionRef.current) requestVersionRef.current += 1;
        };
    }, [normalizedSearch, onSearch]);

    const updateSearch = event => {
        const value = event.target.value;
        requestVersionRef.current += 1;
        setSearch(value);
        setActiveAgent(null);
        if (value.trim().length < 2) {
            setRemoteLoading(false);
            setRemoteResults(null);
            setRemoteCursor('');
            setRemoteHasMore(false);
        }
    };

    const toggleAgents = async () => {
        const next = !agentsOpen;
        setAgentsOpen(next);
        if (next) await onLoadGroups();
    };

    const selectAgent = async group => {
        const requestVersion = ++requestVersionRef.current;
        const selectionVersion = selectionVersionRef.current;
        if (activeAgent?.key === group.key) {
            setRemoteLoading(false);
            setActiveAgent(null);
            setRemoteResults(null);
            return;
        }
        setSearch('');
        setActiveAgent(group);
        setRemoteLoading(true);
        try {
            const payload = await onSearch({agentKey: group.key});
            if (requestVersion !== requestVersionRef.current) return;
            const results = payload.conversations || [];
            setRemoteResults(results);
            setRemoteCursor(payload.next_cursor || '');
            setRemoteHasMore(Boolean(payload.has_more));
            // The activity-sorted feed includes channels beyond the recent-page cache.
            const latestChannel = results.find(conversation => conversation.kind === 'CHANNEL' && !conversation.archived_at);
            if (latestChannel && selectionVersion === selectionVersionRef.current) onSelect(latestChannel);
        } catch {
            if (requestVersion !== requestVersionRef.current) return;
            setRemoteResults([]);
            setRemoteCursor('');
            setRemoteHasMore(false);
        } finally {
            if (requestVersion === requestVersionRef.current) setRemoteLoading(false);
        }
    };

    const loadMore = async () => {
        const requestVersion = ++requestVersionRef.current;
        setRemoteLoading(true);
        try {
            if (remoteResults) {
                const payload = await onSearch({
                    query: normalizedSearch.length >= 2 ? normalizedSearch : undefined,
                    agentKey: activeAgent?.key,
                    cursor: remoteCursor,
                });
                if (requestVersion !== requestVersionRef.current) return;
                setRemoteResults(current => mergeResults(current, payload.conversations || []));
                setRemoteCursor(payload.next_cursor || '');
                setRemoteHasMore(Boolean(payload.has_more));
            } else {
                await onLoadMore();
            }
        } finally {
            if (requestVersion === requestVersionRef.current) setRemoteLoading(false);
        }
    };

    const visibleConversations = useMemo(() => {
        if (!remoteResults) return conversations;
        // Keep the filtered order while reading live summaries updated by read acknowledgements and events.
        const currentById = new Map(conversations.map(conversation => [conversation.id, conversation]));
        return remoteResults.map(conversation => currentById.get(conversation.id) || conversation);
    }, [conversations, remoteResults]);
    const sectionTitle = normalizedSearch.length >= 2
        ? `Search: ${normalizedSearch}`
        : activeAgent?.label || 'Recent';

    return (
        <aside className={`chat-sidebar${open ? ' is-open' : ''}`} aria-label="Chat conversations">
            <div className="chat-sidebar-header">
                <div><FaCommentDots /> <strong>Chat</strong></div>
                <div>
                    {onCreate ? <button type="button" aria-label="New conversation" onClick={() => { selectionVersionRef.current += 1; onCreate(); }}><FaPlus /></button> : null}
                    <button type="button" title="Inbox" aria-label="Work inbox" aria-current={inboxOpen ? 'page' : undefined} onClick={() => { selectionVersionRef.current += 1; onInbox?.(); }}><FaInbox /></button>
                    {onRefresh ? <button type="button" aria-label="Refresh conversations" disabled={refreshing} onClick={onRefresh}><FaSyncAlt className={refreshing ? 'is-spinning' : ''} /></button> : null}
                    <button type="button" className="chat-mobile-only" aria-label="Close conversations" onClick={onClose}><FaTimes /></button>
                </div>
            </div>
            <div className="chat-channel-search">
                <label htmlFor="chat-channel-search">Search channels</label>
                <input id="chat-channel-search" type="search" value={search} onChange={updateSearch} placeholder="Agent, address or worker" />
            </div>
            <section className="chat-agent-browser">
                <button type="button" className="chat-agent-toggle" aria-expanded={agentsOpen} onClick={toggleAgents}>
                    {agentsOpen ? <FaChevronDown /> : <FaChevronRight />} Browse by agent
                </button>
                {agentsOpen ? (
                    <div className="chat-agent-groups">
                        {conversationGroups.map(group => (
                            <button type="button" key={group.key} className={activeAgent?.key === group.key ? 'is-active' : ''} onClick={() => selectAgent(group)}>
                                <span className="chat-agent-label">{group.label}</span>
                                <span className="chat-agent-property-count">{group.count} {group.count === 1 ? 'property' : 'properties'}</span>
                            </button>
                        ))}
                        {!conversationGroups.length ? <div className="chat-sidebar-empty">No agent groups.</div> : null}
                    </div>
                ) : null}
            </section>
            <VirtualConversationSection
                title={sectionTitle}
                conversations={visibleConversations}
                currentUserId={currentUserId}
                selectedId={selectedId}
                onSelect={selectConversation}
                onLoadMore={loadMore}
                hasMore={remoteResults ? remoteHasMore : hasMore}
                loading={remoteLoading || refreshing}
            />
            {!remoteLoading && visibleConversations.length === 0 ? <div className="chat-sidebar-empty">No conversations yet.</div> : null}
        </aside>
    );
}
